// src/components/booking/WaitlistModal.jsx
import { useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../config/firebase";
import { Clock, X, CheckCircle } from "lucide-react";

export default function WaitlistModal({ tenantId, barberId, dateStr, time, onClose }) {
  const [form,    setForm]    = useState({ name: "", email: "" });
  const [saving,  setSaving]  = useState(false);
  const [done,    setDone]    = useState(false);
  const [error,   setError]   = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError("Please enter your name and a valid email.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await addDoc(collection(db, "waitlist"), {
        tenantId,
        barberId,
        dateStr,
        time,
        customer:        { name: form.name.trim(), email: form.email.toLowerCase().trim() },
        status:          "waiting",
        notifyExpiresAt: null,
        createdAt:       serverTimestamp(),
      });
      setDone(true);
    } catch (err) {
      console.error("Waitlist error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/40 backdrop-blur-sm"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-sm animate-fade-in">
        <div className="px-6 py-5 border-b border-surface-100 flex items-start justify-between">
          <div>
            <h2 className="font-semibold text-ink-900 text-lg">Join Waitlist</h2>
            <p className="text-ink-400 text-xs font-sans mt-0.5">
              {time} · {dateStr}
            </p>
          </div>
          <button onClick={onClose} className="text-ink-300 hover:text-ink-600 transition-colors mt-0.5">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5">
          {done ? (
            <div className="flex flex-col items-center text-center py-4 gap-3">
              <CheckCircle size={40} className="text-brand-500" />
              <p className="font-semibold text-ink-800 font-sans">You're on the waitlist!</p>
              <p className="text-ink-400 text-sm font-sans">
                We'll email you at <strong>{form.email}</strong> if this slot opens up.
                You'll have 2 hours to book before the next person is notified.
              </p>
              <button onClick={onClose} className="btn-primary text-sm mt-2">Got it</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
                <Clock size={14} className="shrink-0 text-amber-500 mt-0.5" />
                <p className="text-xs text-amber-700 font-sans">
                  This slot is fully booked. Join the waitlist and we'll notify you immediately if it opens up.
                </p>
              </div>
              <div>
                <label className="form-label">Your name</label>
                <input className="form-input" placeholder="Jane Smith"
                  value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <label className="form-label">Email address</label>
                <input className="form-input" type="email" placeholder="jane@example.com"
                  value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              </div>
              {error && (
                <p className="text-xs text-red-600 font-sans">{error}</p>
              )}
              <button type="submit" disabled={saving}
                className="btn-primary w-full text-sm flex items-center justify-center gap-2">
                {saving
                  ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  : "Join Waitlist"
                }
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
